import { useTranslation } from "react-i18next";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useAlerts } from "context/AlertContext";
import { INotification } from "models/notificationItem.models";
import { IFilterParams } from "models/pagination.models";
import { services } from "services";
import { AlertSeverity } from "utils/constants/alerts.constants";
import {
  QueryAPIKey,
  QueryFetchingStatus,
} from "utils/constants/api.constants";

export type TUseGetNotificationsForUserReturn = {
  status: QueryFetchingStatus;
  error: unknown;
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage: boolean | undefined;
  isError: boolean;
  isSuccess: boolean;
  notifications: INotification[] | undefined;
};

export default function useGetNotificationsForUser(
  userId: string | undefined,
  params: IFilterParams,
): TUseGetNotificationsForUserReturn {
  const { t } = useTranslation("common", {
    keyPrefix: "notifications",
  });

  const { showAlert } = useAlerts();

  const {
    status,
    error,
    fetchNextPage,
    isLoading,
    isError,
    isSuccess,
    hasNextPage,
    data,
  } = useInfiniteQuery({
    queryKey: [QueryAPIKey.GetNotificationsForUser, userId],
    queryFn: ({ pageParam = params.page }) => {
      return services.notifications
        .getNotificationsForUser(userId || "", { ...params, page: pageParam })
        .catch((error) => {
          showAlert({
            severity: AlertSeverity.Error,
            message: t("getNotificationsForUserFailure"),
          });

          throw error;
        });
    },
    initialPageParam: 0,
    getNextPageParam: ({ data }) => (data.last ? undefined : data.number + 1),
    enabled: !!userId,
    select: (data) => data.pages.flatMap((page) => page.data.content),
  });

  return {
    status: status as QueryFetchingStatus,
    error,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isError,
    isSuccess,
    notifications: data,
  };
}
